import {
  type ComponentProps,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  memo,
} from 'react'
import { List, type RowComponentProps } from 'react-window'
import { Box } from '@mui/material'
import type { DropKind, RenderedListRow } from '../types'
import { ListRow } from './ListRow'

/** Fixed row height — matches ListRow compact layout (py 0.6 + 3 text lines + actions). */
export const RESULTS2_LIST_ROW_HEIGHT = 64

/**
 * Row data passed straight through react-window's `rowProps`. react-window shallow-compares these
 * values and re-renders the visible rows whenever one of them changes, so rows can never show
 * outdated data. Keep every value here derived via useMemo/useCallback — never a mutable ref —
 * otherwise changes bypass the comparison and rows go stale again.
 */
export type VirtualizedListRowProps = {
  rendered: RenderedListRow[]
  isTree: boolean
  /** Position of each item in the form `items` array — ListRow subscribes to `items.${index}`. */
  itemGuidToIndex: Record<string, number>
  expanded: Record<string, boolean>
  selectedIdSet: Set<string>
  dragIds: string[]
  dropTarget: { id: string; kind: DropKind } | null
  hideExportButton: boolean
  disableForm: boolean
  showCommentColumn: boolean
  showExtendedCommentColumn: boolean
  qsFullWidth: boolean
  commentPlaceholder: string | undefined
  commentSuggestionKind: 'position' | 'anchor'
  copyParentCrossSectionVisibleByChildId: Record<string, boolean> | undefined
  copyParentCrossSectionLoadingChildId: string | null
  onCopyParentCrossSection: ((childId: string, parentId: string) => void) | undefined
  canShowAddToGroupButton: (itemId: string) => boolean
  toggleExpand: (id: string) => void
  onRowClick: (id: string, event: ReactMouseEvent<HTMLElement>) => void
  startPointerDrag: (id: string) => (e: ReactPointerEvent<HTMLElement>) => void
  addToSelectedGroup: (childId: string) => void
}

const VirtualizedListRow = ({
  index,
  style,
  ariaAttributes,
  rendered,
  isTree,
  itemGuidToIndex,
  expanded,
  selectedIdSet,
  dragIds,
  dropTarget,
  hideExportButton,
  disableForm,
  showCommentColumn,
  showExtendedCommentColumn,
  qsFullWidth,
  commentPlaceholder,
  commentSuggestionKind,
  copyParentCrossSectionVisibleByChildId,
  copyParentCrossSectionLoadingChildId,
  onCopyParentCrossSection,
  canShowAddToGroupButton,
  toggleExpand,
  onRowClick,
  startPointerDrag,
  addToSelectedGroup,
}: RowComponentProps<VirtualizedListRowProps>): ReactElement | null => {
  const entry = rendered[index]
  if (!entry) return null

  const { item, depth, isParent } = entry
  const dropIndicator = isTree && dropTarget?.id === item.id ? dropTarget.kind : null
  const showCopyParentCrossSection =
    !!item.parent &&
    !!onCopyParentCrossSection &&
    (copyParentCrossSectionVisibleByChildId?.[item.id] ||
      copyParentCrossSectionLoadingChildId === item.id)

  return (
    <div {...ariaAttributes} style={style}>
      <ListRow
        index={itemGuidToIndex[item.id]}
        depth={isTree ? depth : 0}
        isParent={isTree ? isParent : false}
        open={expanded[item.id] !== false}
        onToggle={() => toggleExpand(item.id)}
        selected={selectedIdSet.has(item.id)}
        showHandle={isTree}
        reserveHandleColumn={false}
        onClick={event => onRowClick(item.id, event)}
        hideExportButton={hideExportButton}
        hierarchyDisplay={isTree}
        dragging={dragIds.includes(item.id)}
        dropIndicator={dropIndicator}
        onPointerDragStart={isTree ? startPointerDrag(item.id) : undefined}
        onCopyParentCrossSection={
          showCopyParentCrossSection
            ? () => onCopyParentCrossSection?.(item.id, item.parent as string)
            : undefined
        }
        copyParentCrossSectionLoading={copyParentCrossSectionLoadingChildId === item.id}
        showAddToGroupButton={canShowAddToGroupButton(item.id)}
        onAddToGroup={() => addToSelectedGroup(item.id)}
        disableForm={disableForm}
        showCommentColumn={showCommentColumn}
        showExtendedCommentColumn={showExtendedCommentColumn}
        qsFullWidth={qsFullWidth}
        commentPlaceholder={commentPlaceholder}
        commentSuggestionKind={commentSuggestionKind}
      />
    </div>
  )
}

export type VirtualizedResults2ListProps = {
  listRef?: ComponentProps<typeof List>['listRef']
  rowProps: VirtualizedListRowProps
  onRowsRendered?: ComponentProps<typeof List>['onRowsRendered']
}

const VirtualizedResults2List = ({
  listRef,
  rowProps,
  onRowsRendered,
}: VirtualizedResults2ListProps): ReactElement => (
  // react-window v2's List sizes itself to this flex parent (its default style is maxHeight:100% +
  // flexGrow:1 + overflowY:auto) and measures its own element, so no manual height measurement is
  // needed. `minHeight: 0` on the List lets it shrink so its internal scroll engages.
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: 'common.white',
    }}
  >
    <List<VirtualizedListRowProps>
      listRef={listRef}
      rowCount={rowProps.rendered.length}
      rowHeight={RESULTS2_LIST_ROW_HEIGHT}
      rowProps={rowProps}
      rowComponent={VirtualizedListRow}
      overscanCount={6}
      onRowsRendered={onRowsRendered}
      style={{ minHeight: 0 }}
    />
  </Box>
)

export default memo(VirtualizedResults2List)
